import React from "react";
import { pick } from "lodash";
import { Controller, useForm } from "react-hook-form";
import { SUPPORTED_LANGUAGES } from "common/constants";
import { isValidEmail, PHONE_REGEX } from "common/utils/strings";
import { SupportedLanguages } from "common-client/generated/graphql";
import { useUpdatePublicWebsiteSettingsMutation } from "../../../generated/graphql";
import { useStatusToasts } from "../../../hooks/useStatusToasts";
import { track } from "../../../utils/tracking";
import { FlexColumn } from "../../Common/__styles__/Layout";
import { SettingsForm, SettingsGrid } from "../../Common/__styles__/Settings";
import { Button } from "../../Common/Button";
import { SettingsSection } from "../../Common/Settings";
import { Checkbox } from "../../Inputs";
import { ColorPicker } from "../../Inputs/ColorPicker";
import { Text } from "../../Inputs/react-hook-form";
import { RichText } from "../../Inputs/RichText";

const LANGUAGES = [
  {
    label: "English",
    value: SUPPORTED_LANGUAGES.ENGLISH,
  },
  {
    label: "Spanish",
    value: SUPPORTED_LANGUAGES.SPANISH,
  },
];

interface FormStructure {
  name?: Maybe<string>;
  email?: Maybe<string>;
  phoneNumber?: Maybe<string>;
  title?: Maybe<string>;
  homepageTitle: string;
  navBarTitle: string;
  supportedLanguages: Array<SUPPORTED_LANGUAGES>;
  bannerContent?: Maybe<string>;
  bannerColor?: Maybe<string>;
  bannerDisplayed?: Maybe<boolean>;
}

export const PublicWebsiteSettingsForm = ({
  settings,
  canUpdateAccountInformation,
}: {
  settings: FormStructure;
  canUpdateAccountInformation: boolean;
}) => {
  const { addSuccessToast, addErrorToast } = useStatusToasts();

  const {
    handleSubmit,
    register,
    control,
    getValues,
    formState: { errors },
  } = useForm<FormStructure>({
    defaultValues: {
      ...pick(settings, [
        "name",
        "email",
        "phoneNumber",
        "title",
        "homepageTitle",
        "navBarTitle",
        "supportedLanguages",
        "bannerContent",
        "bannerColor",
        "bannerDisplayed",
      ]),
    },
  });

  const trackBannerEnabled = () => {
    const formData = getValues();
    const bannerWasEnabled =
      !settings.bannerDisplayed && formData.bannerDisplayed;

    if (bannerWasEnabled) {
      track("Public Website Banner Enabled", {
        "Banner Color": formData.bannerColor,
        "Banner Content": formData.bannerContent || "",
      });
    }
  };

  const [updatePublicWebsiteSettings, { loading: updating }] =
    useUpdatePublicWebsiteSettingsMutation({
      onCompleted: () => {
        trackBannerEnabled();
        addSuccessToast(
          "Your public website settings were successfully updated.",
        );
      },
      onError: () => {
        addErrorToast(
          "There was a problem updating your public website settings. Please try again or contact support@withforerunner.com",
        );
      },
    });

  const validateTitleMessage =
    "If title is provided you must provide either a name, phone, or email";

  return (
    <SettingsForm
      onSubmit={handleSubmit(async data => {
        await updatePublicWebsiteSettings({ variables: { data } });
      })}
    >
      <SettingsSection
        title="Website title"
        subtitle="This will show up on the public website homepage and navigation bar."
      >
        <SettingsGrid>
          <Text
            label="Homepage title"
            error={errors.homepageTitle?.message}
            {...register("homepageTitle", {
              maxLength: {
                value: 60,
                message: "Homepage title must be less than 60 characters",
              },
            })}
          />
          <Text
            label="Navigation bar title"
            error={errors.navBarTitle?.message}
            {...register("navBarTitle", {
              maxLength: {
                value: 25,
                message: "Navigation bar title must be less than 25 characters",
              },
            })}
          />
        </SettingsGrid>
      </SettingsSection>
      <SettingsSection
        title="Information banner"
        subtitle="This optional banner will appear at the top of all pages on your public website. Use it to communicate urgent information."
      >
        <Controller
          name="bannerDisplayed"
          control={control}
          render={({ field }) => (
            <Checkbox
              name={field.name}
              label="Display banner"
              value={field.value || false}
              onChange={field.onChange}
            />
          )}
        />
        <Controller
          name="bannerContent"
          control={control}
          rules={{
            validate: (val: Maybe<string | undefined>) => {
              if (getValues("bannerDisplayed") && (!val || val.trim() === "")) {
                return "Banner content must be present if Display banner is enabled";
              }
              return;
            },
          }}
          render={({ field, fieldState }) => (
            <RichText
              name={field.name}
              label="Banner content"
              value={field.value || ""}
              onChange={field.onChange}
              error={fieldState.error?.message}
            />
          )}
        />
        <Controller
          name="bannerColor"
          control={control}
          render={({ field, fieldState }) => (
            <ColorPicker
              label="Banner color"
              name={field.name}
              value={field.value || ""}
              onChange={field.onChange}
              error={fieldState.error?.message}
            />
          )}
        />
      </SettingsSection>
      <SettingsSection
        title="Contact information"
        subtitle="This will be displayed at the top of your public website’s Get Help page, allowing residents to contact you directly. For assistance with configuring who receives your “Get Help” emails, please reach out to your account manager or  support@withforerunner.com."
      >
        <SettingsGrid>
          <Text
            label="Name"
            error={errors.name?.message}
            {...register("name")}
          />
          <Text
            label="Title"
            error={errors.title?.message}
            {...register("title", {
              validate: (val: Maybe<string | undefined>) => {
                if (
                  val &&
                  !getValues("name") &&
                  !getValues("email") &&
                  !getValues("phoneNumber")
                ) {
                  return validateTitleMessage;
                }
                return;
              },
            })}
          />
          <Text
            label="Phone number"
            error={errors.phoneNumber?.message}
            {...register("phoneNumber", {
              validate: (val: Maybe<string | undefined>) => {
                if (val && !PHONE_REGEX.test(val)) {
                  return "Phone number is invalid. Please use the following format: 123-456-7890";
                }
                return;
              },
            })}
          />
          <Text
            label="Email address"
            error={errors.email?.message}
            {...register("email", {
              validate: (val: Maybe<string | undefined>) => {
                if (val && !isValidEmail(val)) {
                  return "Email is invalid";
                }
                return;
              },
            })}
          />
        </SettingsGrid>
      </SettingsSection>
      {canUpdateAccountInformation && (
        <SettingsSection
          title="Language"
          subtitle="Select your public website’s supported languages."
        >
          <SettingsGrid>
            <Controller
              control={control}
              name="supportedLanguages"
              render={({ field }) => {
                return (
                  <FlexColumn style={{ gap: "8px" }}>
                    {LANGUAGES.map((language, index) => {
                      return (
                        <Checkbox
                          key={index}
                          value={field.value.includes(language.value)}
                          disabled={
                            language.value === SUPPORTED_LANGUAGES.ENGLISH ?
                              true
                            : false
                          }
                          label={language.label}
                          name={language.value}
                          onChange={value => {
                            if (value) {
                              field.onChange([...field.value!, language.value]);
                            } else {
                              field.onChange(
                                field.value.filter(
                                  (lang: SupportedLanguages) =>
                                    lang !== language.value,
                                ),
                              );
                            }
                          }}
                        />
                      );
                    })}
                  </FlexColumn>
                );
              }}
            />
          </SettingsGrid>
        </SettingsSection>
      )}
      <Button
        styleVariant="primary"
        size="medium"
        type="submit"
        disabled={!canUpdateAccountInformation || updating}
        loading={updating}
      >
        Update
      </Button>
    </SettingsForm>
  );
};
